import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtDateTimeShort, fmtISODate, fmtDateTimeLocal } from "@/lib/dates";
import { Plus, AlertTriangle, ClipboardCheck, MessageSquare, X, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORIES = [
  "Falls and Accidents", "Medication Error", "Behaviour that Challenges", "Injury", "Missing Person",
  "Property Damage", "Near Miss", "Infection / Health Concern", "Environmental Hazard",
  "Abuse or Suspected Abuse", "Unauthorised Absence", "Other",
];

const TABS = [
  { key: "pending", label: "Pending Review" },
  { key: "open", label: "Open" },
  { key: "cqc", label: "CQC Notifiable" },
  { key: "closed", label: "Closed" },
];

const SEVERITY_RANK = { critical: 0, serious: 1, minor: 2 };

export default function Incidents() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [tab, setTab] = useState("open");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    service_user_id: "", date_of_incident: fmtDateTimeLocal(new Date()),
    location: "", category: "Falls and Accidents", severity: "minor",
    description: "", people_involved: "", immediate_action: "",
    emergency_services_contacted: false, family_notified: false,
  });
  const [detail, setDetail] = useState(null);
  const { user } = useAuth();
  const isMgmt = ["registered_manager", "compliance_manager", "nominated_individual", "deputy_manager", "super_admin"].includes(user.role);
  const isRm = ["registered_manager", "deputy_manager", "super_admin"].includes(user.role);

  const load = () => api.get("/incidents").then(({ data }) => setItems(data.items || []));
  useEffect(() => {
    load();
    api.get("/service-users").then(({ data }) => setUsers(data.items || []));
    if (isMgmt) api.get("/staff").then(({ data }) => setStaff(data.items || [])).catch(() => {});
  }, [isMgmt]);

  const submit = async (e) => {
    e.preventDefault();
    const dt = new Date(form.date_of_incident).toISOString();
    await api.post("/incidents", { ...form, date_of_incident: dt });
    setCreating(false);
    setForm({ ...form, description: "", immediate_action: "", location: "", people_involved: "" });
    load();
  };

  // Filter by tab
  const filtered = items
    .filter((i) => {
      if (tab === "pending") return i.status === "open" && !i.assigned_to;
      if (tab === "open") return i.status !== "closed";
      if (tab === "cqc") return i.cqc_notifiable || (i.cqc_checklist && i.cqc_checklist.outcome !== "Not notifiable");
      if (tab === "closed") return i.status === "closed";
      return true;
    })
    .sort((a, b) => {
      const sa = SEVERITY_RANK[a.severity] ?? 9, sb = SEVERITY_RANK[b.severity] ?? 9;
      if (sa !== sb) return sa - sb;
      return (a.date_of_incident || "").localeCompare(b.date_of_incident || "");
    });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Incidents</h1>
          <p className="text-sm text-slate-600">Tabbed incident queue · severity-sorted · CQC Regulation 18 notifiability tracking.</p>
        </div>
        {user.role !== "family_member" && (
          <button onClick={() => setCreating((v) => !v)} data-testid="new-incident-button" className="inline-flex items-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium min-h-[44px]">
            <Plus className="h-4 w-4" /> New incident
          </button>
        )}
      </div>

      {creating && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="incident-form">
          <select required value={form.service_user_id} onChange={(e) => setForm({ ...form, service_user_id: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" data-testid="inc-su-select">
            <option value="">Select service user…</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
          <input type="datetime-local" required value={form.date_of_incident} onChange={(e) => setForm({ ...form, date_of_incident: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" />
          <input required placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" data-testid="inc-severity-select">
            <option value="minor">Minor</option><option value="serious">Serious</option><option value="critical">Critical</option>
          </select>
          <input placeholder="People involved" value={form.people_involved} onChange={(e) => setForm({ ...form, people_involved: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" />
          <textarea required minLength={50} placeholder="Description (min 50 characters)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-md" data-testid="inc-description" />
          <textarea required placeholder="Immediate action taken" value={form.immediate_action} onChange={(e) => setForm({ ...form, immediate_action: e.target.value })} rows={2} className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-md" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.emergency_services_contacted} onChange={(e) => setForm({ ...form, emergency_services_contacted: e.target.checked })} /> Emergency services contacted</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.family_notified} onChange={(e) => setForm({ ...form, family_notified: e.target.checked })} /> Family notified</label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 border border-slate-300 rounded-md">Cancel</button>
            <button type="submit" data-testid="inc-submit-button" className="px-4 py-2 bg-[#0D7377] text-white rounded-md font-medium">Submit incident</button>
          </div>
        </form>
      )}

      {/* Tab queue */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="border-b border-slate-200 flex gap-1 px-2 overflow-x-auto" data-testid="incident-tabs">
          {TABS.map((t) => {
            const count = items.filter((i) => {
              if (t.key === "pending") return i.status === "open" && !i.assigned_to;
              if (t.key === "open") return i.status !== "closed";
              if (t.key === "cqc") return i.cqc_notifiable || (i.cqc_checklist && i.cqc_checklist.outcome !== "Not notifiable");
              if (t.key === "closed") return i.status === "closed";
              return false;
            }).length;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`incident-tab-${t.key}`} className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${tab === t.key ? "text-[#0D7377] border-[#0D7377]" : "text-slate-600 border-transparent hover:text-slate-900"}`}>
                {t.label} <span className="ml-1 text-xs bg-slate-100 px-1.5 py-0.5 rounded">{count}</span>
              </button>
            );
          })}
        </div>
        <table className="w-full text-sm" data-testid="incidents-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
              <th className="text-left py-2 px-3">Date</th>
              <th className="text-left py-2 px-3">Category</th>
              <th className="text-left py-2 px-3">Severity</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-left py-2 px-3">Notifiable</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(i)} data-testid={`incident-row-${i.id}`}>
                <td className="py-3 px-3 text-slate-700">{fmtDateTimeShort(i.date_of_incident)}</td>
                <td className="py-3 px-3">
                  <p className="text-slate-900 font-medium flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />{i.category}</p>
                  <p className="text-xs text-slate-500 line-clamp-1">{i.description}</p>
                </td>
                <td className="py-3 px-3"><StatusBadge status={i.severity} /></td>
                <td className="py-3 px-3"><StatusBadge status={i.status} /></td>
                <td className="py-3 px-3">{i.cqc_checklist?.outcome ? <StatusBadge status={i.cqc_checklist.outcome === "Notifiable" ? "critical" : i.cqc_checklist.outcome === "Possibly notifiable" ? "in_progress" : "active"} label={i.cqc_checklist.outcome} /> : <span className="text-xs text-slate-400">Not assessed</span>}</td>
                <td className="py-3 px-3 text-right text-slate-400"><ChevronRight className="h-4 w-4 inline" /></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-500">No incidents in this tab.</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && <IncidentDetailModal incident={detail} staff={staff} onClose={() => setDetail(null)} reload={() => { load(); }} isMgmt={isMgmt} isRm={isRm} />}
    </div>
  );
}

function IncidentDetailModal({ incident, staff, onClose, reload, isMgmt, isRm }) {
  const [tab, setTab] = useState("timeline");
  const [noteText, setNoteText] = useState("");
  const [notes, setNotes] = useState(incident.update_notes || []);
  const [assignTo, setAssignTo] = useState("");
  const [dueDate, setDueDate] = useState(fmtISODate(new Date(Date.now() + 3 * 86400000)));
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [outcome, setOutcome] = useState(incident.cqc_checklist?.outcome || null);
  const [savingChecklist, setSavingChecklist] = useState(false);
  const [busy, setBusy] = useState(false);
  const [closeForm, setCloseForm] = useState({
    root_cause: "",
    contributing_factors: "",
    actions_taken: "",
    lessons_learned: "",
    outcome: "Resolved — no further action",
    cqc_notifiable: incident.cqc_checklist?.outcome === "Notifiable",
    cqc_reference: incident.cqc_reference || "",
  });
  const [closeErr, setCloseErr] = useState("");

  const submitClose = async (e) => {
    e.preventDefault();
    setCloseErr("");
    if (!closeForm.root_cause.trim() || closeForm.root_cause.trim().length < 10) {
      setCloseErr("Root cause is required (min 10 characters).");
      return;
    }
    if (!closeForm.actions_taken.trim() || closeForm.actions_taken.trim().length < 10) {
      setCloseErr("Actions taken is required (min 10 characters).");
      return;
    }
    if (closeForm.cqc_notifiable && !closeForm.cqc_reference.trim()) {
      setCloseErr("CQC reference is required when the incident is marked notifiable.");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/incidents/${incident.id}/investigation`, closeForm);
      reload();
      onClose();
    } catch (err) {
      setCloseErr(err?.response?.data?.detail || err.message || "Failed to close incident");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (isMgmt && tab === "cqc") {
      api.get("/incidents/cqc-checklist/questions").then(({ data }) => setQuestions(data.questions || []));
    }
  }, [tab, isMgmt]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/incidents/${incident.id}/notes`, { note_text: noteText });
      setNotes([...(notes || []), data.note]);
      setNoteText("");
    } finally { setBusy(false); }
  };

  const assign = async (e) => {
    e.preventDefault();
    if (!assignTo) return;
    setBusy(true);
    try {
      await api.put(`/incidents/${incident.id}/assign`, { assigned_to: assignTo, due_date: new Date(dueDate).toISOString() });
      reload();
      onClose();
    } finally { setBusy(false); }
  };

  const submitChecklist = async (e) => {
    e.preventDefault();
    setSavingChecklist(true);
    try {
      const { data } = await api.post(`/incidents/${incident.id}/cqc-checklist`, { answers });
      setOutcome(data.outcome);
      reload();
    } finally { setSavingChecklist(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="incident-detail-modal">
        <header className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">{incident.category}</p>
            <h3 className="font-heading text-xl font-semibold text-slate-900">{incident.severity?.toUpperCase()} incident</h3>
            <p className="text-xs text-slate-500 mt-1">{fmtDateTimeShort(incident.date_of_incident)} · reported by {incident.reporter_name}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-5 w-5" /></button>
        </header>
        <nav className="px-5 border-b border-slate-200 flex gap-1 flex-wrap">
          {[
            ["timeline", "Timeline & Notes"],
            isMgmt && ["assign", "Assign"],
            isMgmt && ["cqc", "CQC Checklist"],
            isMgmt && incident.status !== "closed" && ["close", "Close incident"],
            isMgmt && incident.status === "closed" && ["closed", "Closure summary"],
          ].filter(Boolean).map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`detail-tab-${k}`} className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === k ? "border-[#0D7377] text-[#0D7377]" : "border-transparent text-slate-600"}`}>{l}</button>
          ))}
        </nav>

        <div className="flex-1 overflow-y-auto p-5 scrollbar-thin">
          {tab === "timeline" && (
            <div className="space-y-4">
              <section className="bg-slate-50 border border-slate-200 rounded-md p-4">
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Original report (immutable)</p>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{incident.description}</p>
                <p className="mt-2 text-xs text-slate-500"><strong>Immediate action:</strong> {incident.immediate_action}</p>
              </section>
              <section>
                <h4 className="font-heading font-semibold text-slate-900 mb-2 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Update notes timeline</h4>
                <ul className="space-y-2 mb-3" data-testid="incident-notes-timeline">
                  {(notes || []).length === 0 && <li className="text-sm text-slate-500">No update notes yet.</li>}
                  {(notes || []).map((n) => (
                    <li key={n.id} className="border-l-2 border-[#0D7377] pl-3 py-1">
                      <p className="text-xs text-slate-500">{fmtDateTimeShort(n.added_at)} · {n.added_by_name} ({n.added_by_role?.replace(/_/g, " ")})</p>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.note_text}</p>
                    </li>
                  ))}
                </ul>
                {incident.status !== "closed" && (
                  <form onSubmit={addNote} className="space-y-2" data-testid="add-incident-note-form">
                    <textarea required minLength={5} value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} placeholder="Add an update note…" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="incident-note-textarea" />
                    <button type="submit" disabled={busy} className="px-4 py-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white rounded-md text-sm font-medium" data-testid="incident-add-note-button">Add note</button>
                  </form>
                )}
              </section>
            </div>
          )}

          {tab === "assign" && isMgmt && (
            <form onSubmit={assign} className="space-y-3 max-w-md" data-testid="incident-assign-form">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500">Assign to investigator</label>
                <select required value={assignTo} onChange={(e) => setAssignTo(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md">
                  <option value="">Choose…</option>
                  {staff.filter((s) => ["registered_manager", "deputy_manager", "team_leader"].includes(s.role)).map((s) => (
                    <option key={s.id} value={s.id}>{s.full_name} — {s.role.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500">Investigation due by</label>
                <input type="date" required value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md" />
                <p className="text-xs text-slate-500 mt-1">Recommended: minor 5d · serious 3d · critical 1d</p>
              </div>
              <button type="submit" disabled={busy || !isRm} data-testid="incident-assign-button" className="w-full bg-[#0D7377] hover:bg-[#0A5C5F] text-white py-2 rounded-md font-medium">Assign &amp; mark under investigation</button>
              {!isRm && <p className="text-xs text-amber-700">Only Registered Manager / Deputy can assign.</p>}
            </form>
          )}

          {tab === "cqc" && isMgmt && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Complete this checklist to determine whether the incident is notifiable to the CQC under Regulation 18.</p>
              {outcome && <div className={`p-3 rounded-md text-sm font-medium ${outcome === "Notifiable" ? "bg-red-50 border border-red-200 text-red-800" : outcome === "Possibly notifiable" ? "bg-amber-50 border border-amber-200 text-amber-900" : "bg-emerald-50 border border-emerald-200 text-emerald-800"}`} data-testid="cqc-outcome">Outcome: {outcome}</div>}
              <form onSubmit={submitChecklist} className="space-y-2" data-testid="cqc-checklist-form">
                {questions.map((q) => (
                  <div key={q.key} className="border border-slate-200 rounded-md p-3">
                    <p className="text-sm text-slate-800 mb-2">{q.text}</p>
                    <div className="flex gap-2">
                      <label className="flex items-center gap-1 text-sm"><input type="radio" name={q.key} required onChange={() => setAnswers({ ...answers, [q.key]: true })} /> Yes</label>
                      <label className="flex items-center gap-1 text-sm"><input type="radio" name={q.key} onChange={() => setAnswers({ ...answers, [q.key]: false })} /> No</label>
                    </div>
                  </div>
                ))}
                <button type="submit" disabled={savingChecklist || !isRm} data-testid="cqc-checklist-submit" className="w-full bg-[#0D7377] hover:bg-[#0A5C5F] text-white py-2 rounded-md font-medium inline-flex items-center justify-center gap-2"><ClipboardCheck className="h-4 w-4" /> Save assessment</button>
                {!isRm && <p className="text-xs text-amber-700">Only Registered Manager / Deputy can submit.</p>}
              </form>
            </div>
          )}
          {tab === "close" && isMgmt && incident.status !== "closed" && (
            <form onSubmit={submitClose} className="space-y-3 max-w-2xl" data-testid="incident-close-form">
              <p className="text-sm text-slate-600">Closing an incident captures the investigation outcome and produces a permanent audit record. Notes cannot be added after closure.</p>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Root cause <span className="text-rose-600">*</span></label>
                <textarea
                  required minLength={10} rows={3}
                  value={closeForm.root_cause}
                  onChange={(e) => setCloseForm({ ...closeForm, root_cause: e.target.value })}
                  placeholder="What was the underlying cause? (min 10 chars)"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="close-root-cause"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Contributing factors</label>
                <textarea
                  rows={2}
                  value={closeForm.contributing_factors}
                  onChange={(e) => setCloseForm({ ...closeForm, contributing_factors: e.target.value })}
                  placeholder="E.g. staffing levels, environmental hazards, communication gap"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="close-contributing"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Actions taken <span className="text-rose-600">*</span></label>
                <textarea
                  required minLength={10} rows={3}
                  value={closeForm.actions_taken}
                  onChange={(e) => setCloseForm({ ...closeForm, actions_taken: e.target.value })}
                  placeholder="What corrective actions were implemented?"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="close-actions-taken"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Lessons learned</label>
                <textarea
                  rows={2}
                  value={closeForm.lessons_learned}
                  onChange={(e) => setCloseForm({ ...closeForm, lessons_learned: e.target.value })}
                  placeholder="What will the team do differently going forward?"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="close-lessons"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Outcome <span className="text-rose-600">*</span></label>
                <select
                  value={closeForm.outcome}
                  onChange={(e) => setCloseForm({ ...closeForm, outcome: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="close-outcome"
                >
                  <option>Resolved — no further action</option>
                  <option>Resolved — care plan updated</option>
                  <option>Resolved — staff retrained</option>
                  <option>Resolved — referred to safeguarding</option>
                  <option>Resolved — referred to GP / health</option>
                  <option>Resolved — other (detail in lessons)</option>
                </select>
              </div>
              <fieldset className="border border-slate-200 rounded-md p-3">
                <legend className="text-xs uppercase tracking-wider text-slate-500 px-1 font-semibold">CQC Regulation 18</legend>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={closeForm.cqc_notifiable}
                    onChange={(e) => setCloseForm({ ...closeForm, cqc_notifiable: e.target.checked })}
                    data-testid="close-cqc-notifiable"
                  />
                  Incident is notifiable to the CQC
                </label>
                {closeForm.cqc_notifiable && (
                  <input
                    type="text"
                    required
                    value={closeForm.cqc_reference}
                    onChange={(e) => setCloseForm({ ...closeForm, cqc_reference: e.target.value })}
                    placeholder="CQC reference number (required)"
                    className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    data-testid="close-cqc-reference"
                  />
                )}
              </fieldset>
              {closeErr && <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="close-error">{closeErr}</p>}
              <button
                type="submit"
                disabled={busy || !isRm}
                data-testid="close-incident-button"
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white py-2.5 rounded-md font-semibold"
              >
                {busy ? "Closing…" : "Close incident"}
              </button>
              {!isRm && <p className="text-xs text-amber-700">Only Registered Manager / Deputy can close incidents.</p>}
            </form>
          )}

          {tab === "closed" && incident.status === "closed" && (
            <div className="space-y-3" data-testid="incident-closure-summary">
              <p className="text-xs text-emerald-700 inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                Closed {fmtDateTimeShort(incident.closed_at)}
              </p>
              {incident.investigation && Object.entries({
                "Root cause": incident.investigation.root_cause,
                "Contributing factors": incident.investigation.contributing_factors,
                "Actions taken": incident.investigation.actions_taken,
                "Lessons learned": incident.investigation.lessons_learned,
                "Outcome": incident.outcome,
                "CQC notifiable": incident.cqc_notifiable ? `Yes — ref ${incident.cqc_reference || "n/a"}` : "No",
              }).map(([k, v]) => (
                <div key={k}>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{k}</p>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{v || "—"}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
